import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Steps } from '@/components/steps';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import useDraftsHandler from '../../../../../utils/controllers/useDraftsHandler';
import agentData from '../../../../../constants/agents/initialStates/agentData';
import useAgentDesignValidator from '../controllers/useAgentDesignValidator';
import useAgentDefineValidator from '../controllers/useAgentDefineValidator';
import useAgentHandler from '../../components/publish/controllers/useAgentHandler';
import useAIAgentHandler from '../../components/aiAgentDefine/controllers/useAIAgentHandler';
import {
  setAgentStep,
  setAiEngagementsNeedUpdate,
} from '../../../../../redux/slices/agents/agentSlice';
import useAgentGoalValidator from '../controllers/useAgentGoalValidator';
import useAIAgentDefineValidator from '../controllers/useAIAgentDefineValidator';
import useSmartReplyHandler from '../../components/smartReply/controllers/useSmartReplyHandler';
import useSmartReplyValidator from '../controllers/useSmartReplyValidator';
import useQualificationValidator from '../../components/qualification/controllers/useQualificationValidator';
import useQualificationHandler from '../../components/qualification/controllers/useQualificationHandler';
import usePersonalizationAgentValidator from '../../components/personalizationAgent/controllers/usePersonalizationAgentValidator';
import useAbTestVariantEdit from '../controllers/useAbTestVariantEdit';
import { AB_TEST_VARIANT_EDIT_LABEL } from '../../../../../constants/agents/abTestVariantEdit';
import { selectIsEventsPlatform } from '../../../../../redux/slices/platform/platformSlice';
import {
  selectSelectedEvent,
  selectSelectedWorkflowKey,
} from '../../../../../redux/slices/events/eventsSlice';
import {
  getEventContextPath,
  getEventDetailPath,
  getEventWorkflowAgentsPath,
} from '../../../../events/controllers/eventFormatters';
import useEventDetailHeader from '../../../../events/controllers/useEventDetailHeader';
import EventContextTrail from '../../../../events/components/EventContextTrail';

function AgentTopBar() {
  const isAgentEditLoading = useSelector(state => state.agent.etc.isAgentEditLoading);
  const [isAiEngagementsModalOpen, setIsAiEngagementsModalOpen] = useState(false);
  const aiEngagementsNeedUpdate = useSelector(state => state.agent.data.aiEngagementsNeedUpdate);
  const xrayQuestions = useSelector(state => state.agent.data.campaignData?.xrayQuestions);

  const dispatch = useDispatch();
  const { onSubmitHandler, isUpdating } = useAgentHandler();
  const { onSubmitHandler: onAIAgentSubmitHandler, isUpdating: isAIAgentUpdating } =
    useAIAgentHandler();
  const { onSubmitHandler: onSmartReplySubmitHandler, isUpdating: isSmartReplyUpdating } =
    useSmartReplyHandler();
  const { onSubmitHandler: onQualificationSubmitHandler, isUpdating: isQualificationUpdating } =
    useQualificationHandler();
  const { isAgentDesignNextButtonDisabled } = useAgentDesignValidator();
  const { isDefineNextButtonDisabled } = useAgentDefineValidator();
  const { isGoalNextButtonDisabled } = useAgentGoalValidator();
  const { isAIAgentDefineNextButtonDisabled } = useAIAgentDefineValidator();
  const { isSmartReplyDesignNextButtonDisabled, isSmartReplyDefineNextButtonDisabled } =
    useSmartReplyValidator();
  const { isQualificationDefineNextButtonDisabled } = useQualificationValidator();
  const { isPersonalizationDefineNextButtonDisabled, isPersonalizationDesignNextButtonDisabled } =
    usePersonalizationAgentValidator();
  const currentStep = useSelector(state => state.agent.etc.agentStep);
  const creationType = useSelector(state => state.agent.etc.creationType);
  const agentTitle = useSelector(state => state.agent.data.title);
  const aiAgentTitle = useSelector(state => state.agent.aiAgent?.title);
  const smartReplyTitle = useSelector(state => state.agent.smartReply?.title);
  const qualificationTitle = useSelector(state => state.agent.qualificationAgent?.title);
  const agentType = useSelector(state => state.agent.etc.agentType);
  const isPaused = useSelector(state => state.agent?.data?.isPaused);
  const engagementGenerationByAI = useSelector(
    state => state.agent.data.campaignData.engagementGenerationByAI
  );
  const campaignType = useSelector(state => state.agent.data.campaignData.campaignType);
  const currentSmartReplyType = useSelector(
    state => state?.agent?.smartReply?.smartReplyData?.type
  );

  const navigate = useNavigate();
  const { pathname } = useLocation();
  // While an edit / draft is loading, creationType can still hold the previous flow's value
  // (or nothing on a direct link), so the header follows the route until the agent is in.
  const routeCreationType = pathname.startsWith('/agents/edit')
    ? 'edit'
    : pathname.startsWith('/agents/draft')
      ? 'draft'
      : null;
  const headerCreationType = routeCreationType || creationType;
  const { isAbTestVariantEdit, isAbTestRedirect } = useAbTestVariantEdit();
  const isEventsPlatform = useSelector(selectIsEventsPlatform);
  const selectedEvent = useSelector(selectSelectedEvent);
  const selectedWorkflowKey = useSelector(selectSelectedWorkflowKey);
  const eventsNav = useEventDetailHeader();
  const eventBackPath =
    isEventsPlatform && selectedEvent?.id
      ? getEventContextPath(selectedEvent.id, selectedWorkflowKey)
      : null;
  const useEventsNav = Boolean(eventBackPath) && !isAbTestRedirect;
  const eventTrailItems = useEventsNav
    ? [
        {
          label: eventsNav.eventName,
          path: eventsNav.eventId ? getEventDetailPath(eventsNav.eventId) : null,
        },
        {
          label: eventsNav.workflowBreadcrumbTitle,
          path:
            eventsNav.eventId && eventsNav.workflowKey
              ? getEventWorkflowAgentsPath(eventsNav.eventId, eventsNav.workflowKey)
              : null,
        },
      ]
    : [];
  const backPath = isAbTestRedirect
    ? agentData.abTestPath
    : eventBackPath || agentData.dashboardPath;

  const { handleCreateDraft, isCreatingDraft } = useDraftsHandler({
    draftType: agentData?.draft?.value,
    navigateBack: backPath,
  });

  const onClickHandleNext = () => {
    if (agentType === 'aiAgent') {
      if (isAIAgentDefineNextButtonDisabled()) {
        return;
      } else {
        onAIAgentSubmitHandler();
      }
    } else if (agentType === 'smartReply') {
      if (isSmartReplyDefineNextButtonDisabled() || isSmartReplyDesignNextButtonDisabled()) {
        return;
      } else {
        onSmartReplySubmitHandler();
      }
    } else if (agentType === 'qualification') {
      if (isQualificationDefineNextButtonDisabled()) {
        return;
      } else {
        onQualificationSubmitHandler();
      }
    } else if (agentType === 'ptEmailChannel') {
      // Define/Design/Publish are placeholders for now — no validation or submission yet.
      toast.info('Coming soon', {
        description: 'Personalisation Agent creation isn’t fully wired up yet.',
      });
    } else {
      if (isAgentDesignNextButtonDisabled() || isDefineNextButtonDisabled()) {
        return;
      } else {
        if (
          (campaignType === 'rag' && aiEngagementsNeedUpdate === true) ||
          (aiEngagementsNeedUpdate !== undefined && engagementGenerationByAI)
        ) {
          setIsAiEngagementsModalOpen(true);
        } else {
          onSubmitHandler({ isLaunch: !isPaused });
        }
      }
    }
  };

  const handleStepChange = step => {
    if (xrayQuestions && Array.isArray(xrayQuestions)) {
      const excludedTypes = ['relatedTopics', 'linkActions', 'relatedAuthors'];
      const hasMissingGuideline = xrayQuestions.some(
        question =>
          question.isIncluded &&
          !excludedTypes.includes(question.type) &&
          (!question.userGuideline || question.userGuideline.trim() === '')
      );

      if (hasMissingGuideline) {
        toast.warning('Add a user guideline to every included question');
        return;
      }
    }
    if (agentType === 'aiAgent') {
      const getNextStep = step => {
        if (step >= 2) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isAIAgentDefineNextButtonDisabled()) return 1;
          return step;
        }
        if (isGoalNextButtonDisabled()) return 0;
        return step;
      };
      dispatch(setAgentStep(getNextStep(step)));
      return;
    }
    if (agentType === 'smartReply') {
      const getNextStep = step => {
        if (step >= 3) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isSmartReplyDefineNextButtonDisabled()) return 1;
          if (isSmartReplyDesignNextButtonDisabled()) return 2;
          return step;
        }
        if (step >= 2) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isSmartReplyDefineNextButtonDisabled()) return 1;
          return step;
        }
        if (isGoalNextButtonDisabled()) return 0;
        return step;
      };
      dispatch(setAgentStep(getNextStep(step)));
      return;
    }
    if (agentType === 'qualification') {
      const getNextStep = step => {
        if (step >= 1) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isQualificationDefineNextButtonDisabled()) return 1;
          return step;
        }
        if (isGoalNextButtonDisabled()) return 0;
        return step;
      };
      dispatch(setAgentStep(getNextStep(step)));
      return;
    }
    if (agentType === 'ptEmailChannel') {
      const getNextStep = step => {
        if (step >= 3) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isPersonalizationDefineNextButtonDisabled()) return 1;
          if (isPersonalizationDesignNextButtonDisabled()) return 2;
          return step;
        }
        if (step >= 2) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isPersonalizationDefineNextButtonDisabled()) return 1;
          return step;
        }
        if (isGoalNextButtonDisabled()) return 0;
        return step;
      };
      dispatch(setAgentStep(getNextStep(step)));
      return;
    } else {
      const getNextStep = step => {
        if (step >= 3) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isDefineNextButtonDisabled()) return 1;
          if (isAgentDesignNextButtonDisabled()) return 2;
          return step;
        }
        if (step >= 2) {
          if (isGoalNextButtonDisabled()) return 0;
          if (isDefineNextButtonDisabled()) return 1;
          return step;
        }
        if (isGoalNextButtonDisabled()) return 0;
        return step;
      };
      dispatch(setAgentStep(getNextStep(step)));
      return;
    }
  };

  const getStepItems = () => {
    const steps =
      agentType === 'aiAgent'
        ? agentData.aiAgentSteps
        : agentType === 'qualification'
          ? agentData.qualificationSteps
          : agentData.steps;
    return steps?.map((step, idx) => ({
      ...step,
      title: <span>{step.title}</span>,
      plainTitle: step.title,
      disabled:
        idx === 0 &&
        (headerCreationType === 'edit' ||
          headerCreationType === 'agentEdit' ||
          headerCreationType === 'draft')
          ? true
          : false,
    }));
  };

  const isSubmitEditLoading =
    isUpdating || isAIAgentUpdating || isSmartReplyUpdating || isQualificationUpdating;

  const stepItems = getStepItems();

  // Title says what is happening; navigation back lives in the breadcrumb.
  const headerTitle = isAbTestVariantEdit
    ? `Edit ${AB_TEST_VARIANT_EDIT_LABEL}`
    : {
        create: 'New agent',
        agentCreate: 'New agent',
        edit: 'Edit agent',
        agentEdit: 'Edit agent',
        draft: 'Finish draft',
      }[headerCreationType];
  const isCreateHeader = headerCreationType === 'create' || headerCreationType === 'agentCreate';
  const agentDisplayName =
    (agentType === 'aiAgent'
      ? aiAgentTitle
      : agentType === 'qualification'
        ? qualificationTitle
        : agentType === 'smartReply'
          ? smartReplyTitle || agentTitle
          : agentTitle) || '';
  const headerLead = isCreateHeader
    ? 'Pick what it does, where it runs and how it looks, then publish.'
    : agentDisplayName.trim() ||
      (isAgentEditLoading || creationType !== headerCreationType ? null : 'Untitled agent');

  const hideDraftButton =
    agentType === 'aiAgent' ||
    creationType === 'draft' ||
    agentType === 'smartReply' ||
    agentType === 'qualification' ||
    agentType === 'ptEmailChannel' ||
    ((currentSmartReplyType === 'email' || currentSmartReplyType === 'whatsapp') &&
      campaignType === 'rag');

  const headerActions =
    headerCreationType === 'edit' ? (
      <div className="ms-auto flex items-center gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(backPath)}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onClickHandleNext}
          disabled={isSubmitEditLoading || isAgentEditLoading || creationType !== 'edit'}
        >
          {isSubmitEditLoading && <Spinner data-icon="inline-start" />}
          {isAbTestVariantEdit ? `Save ${AB_TEST_VARIANT_EDIT_LABEL} changes` : 'Save changes'}
        </Button>
      </div>
    ) : headerCreationType === 'draft' ? (
      <Button
        type="button"
        variant="outline"
        className="ms-auto"
        onClick={() => navigate(agentData?.draftDashboardPath)}
      >
        Cancel
      </Button>
    ) : hideDraftButton ? null : (
      <Button
        type="button"
        variant="outline"
        onClick={handleCreateDraft}
        disabled={isCreatingDraft || currentStep === 0}
        title={currentStep === 0 ? 'Pick a goal first' : undefined}
        className="ms-auto"
      >
        {isCreatingDraft && <Spinner data-icon="inline-start" />}
        Save as draft
      </Button>
    );

  const stepsAndActions = (
    <div className="flex w-full flex-wrap items-center gap-x-6 gap-y-3 lg:w-auto">
      <p className="min-w-0 truncate text-sm text-muted-foreground tabular-nums sm:hidden">
        Step {Math.min(currentStep, stepItems.length - 1) + 1} of {stepItems.length}
        <span className="font-medium text-foreground">
          {' · '}
          {stepItems[Math.min(currentStep, stepItems.length - 1)]?.plainTitle}
        </span>
      </p>
      <Steps
        current={currentStep}
        size="sm"
        items={stepItems}
        responsive={false}
        onChange={e => {
          if (isAgentEditLoading) return;
          handleStepChange(e);
        }}
        className={cn(
          'hidden min-w-0 flex-1 sm:flex lg:w-[420px] lg:flex-none',
          isAgentEditLoading && 'opacity-50'
        )}
      />
      {headerActions}
    </div>
  );

  return (
    <div>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
          <div className="flex min-w-0 flex-1 basis-72 flex-col gap-1">
            {/* Inside an event: which event and workflow this agent belongs to. */}
            {useEventsNav && <EventContextTrail items={eventTrailItems} />}
            <h1 className="text-2xl leading-tight font-bold tracking-tight text-balance text-foreground md:text-3xl">
              {headerTitle || <Skeleton className="h-8 w-48" />}
            </h1>
            {headerLead ? (
              <p
                className={cn(
                  'truncate text-sm text-muted-foreground',
                  isCreateHeader && 'hidden sm:block'
                )}
                title={isCreateHeader ? undefined : headerLead}
              >
                {headerLead}
              </p>
            ) : !isCreateHeader ? (
              <Skeleton className="mt-1 h-4 w-40" />
            ) : null}
          </div>

          {stepsAndActions}
        </div>
      </div>

      <Dialog
        open={isAiEngagementsModalOpen}
        onOpenChange={next => {
          if (!next) setIsAiEngagementsModalOpen(false);
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Apply your changes to past AI engagements?</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-pretty text-muted-foreground">
            You changed a setting that affects engagements the AI already generated. Choose whether
            to update them all, or keep them as they are and use the change from now on.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsAiEngagementsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAiEngagementsModalOpen(false);
                dispatch(setAiEngagementsNeedUpdate(false));
                onSubmitHandler({ isLaunch: !isPaused, aiEngagementsNeedUpdate: false });
              }}
            >
              Keep existing
            </Button>
            <Button
              type="button"
              onClick={() => {
                setIsAiEngagementsModalOpen(false);
                dispatch(setAiEngagementsNeedUpdate(true));
                onSubmitHandler({ isLaunch: !isPaused, aiEngagementsNeedUpdate: true });
              }}
            >
              Apply to all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AgentTopBar;
